import type { Cents } from "./money.ts";

export interface Contribution {
  seat: number;
  /** Total put in the pot this hand. */
  amount: Cents;
  folded: boolean;
}

export interface Pot {
  amount: Cents;
  /** Seats still in the hand that can win this pot. */
  eligible: number[];
}

/**
 * Builds the main pot and side pots from each player's total contribution.
 * Folded players' chips stay in the pots but they are never eligible.
 * A pot with a single eligible seat is an uncalled bet that simply goes back to that seat.
 */
export function buildPots(contribs: readonly Contribution[]): Pot[] {
  const live = contribs.filter((c) => !c.folded && c.amount > 0);
  const levels = [...new Set(live.map((c) => c.amount))].sort((a, b) => a - b);
  const pots: Pot[] = [];
  let prev = 0;
  for (const level of levels) {
    let amount = 0;
    for (const c of contribs) amount += Math.max(0, Math.min(c.amount, level) - prev);
    const eligible = live.filter((c) => c.amount >= level).map((c) => c.seat);
    if (amount > 0) pots.push({ amount, eligible });
    prev = level;
  }
  // Chips a folded player put in above every live player's total (rare): add to the last pot.
  let excess = 0;
  for (const c of contribs) excess += Math.max(0, c.amount - prev);
  if (excess > 0 && pots.length > 0) pots[pots.length - 1]!.amount += excess;

  // Merge neighbouring pots with identical eligibility
  const merged: Pot[] = [];
  for (const p of pots) {
    const last = merged[merged.length - 1];
    if (last && last.eligible.length === p.eligible.length && last.eligible.every((s, i) => s === p.eligible[i])) {
      last.amount += p.amount;
    } else merged.push({ ...p, eligible: [...p.eligible] });
  }
  return merged;
}
